import React, { useRef, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";

import IntroImage from "../../Assets/Images/LandingPage/Intro_img.png";
import VideoIconImg from "../../Assets/Images/LandingPage/video_icon.svg";
import CustomButton from "Components/Commons/CustomButton/CustomButton";

import { WhoWeAreContainer } from "./WhoWeAre.style";

import AboutUsIntroVideo from "../../Assets/Sounds/HomePageIntro.mp4";

// Reusable IntroBox component
const IntroBox = ({ frontTitle, frontText, backText, bgColor }) => {
	const [hover, setHover] = useState(false);
	const boxRef = useRef(null);
	const frontRef = useRef(null);
	const backRef = useRef(null);
	const [boxHeight, setBoxHeight] = useState(280);

	// Dynamically calculate height based on content
	useEffect(() => {
		const frontHeight = frontRef.current?.scrollHeight || 0;
		const backHeight = backRef.current?.scrollHeight || 0;
		setBoxHeight(Math.max(280, frontHeight, backHeight));
	}, []);

	return (
		<div
			ref={boxRef}
			className="intro-box"
			style={{
				minHeight: "280px",
				height: boxHeight,
				perspective: "1000px",
			}}
			onMouseEnter={() => setHover(true)}
			onMouseLeave={() => setHover(false)}
		>
			<div className="intro-inner-wrapper" style={{ background: bgColor }}>
				<div className={`intro-inner ${hover ? "flipped" : ""}`}>
					{/* Front Side */}
					<div ref={frontRef} className="front">
						<div className="in_frontback_content">
							<h3>{frontTitle}</h3>
							<p>{frontText}</p>
						</div>
					</div>

					{/* Back Side */}
					<div ref={backRef} className="back">
						<div className="in_frontback_content">
							<h3>{frontTitle}</h3>
							<p>{backText}</p>
						</div>
					</div>
				</div>
			</div>
		</div>
	);
};

const WhoWeAre = () => {
	const navigate = useNavigate();
	const videoRef = useRef(null);

	const [isPlaying, setIsPlaying] = useState(false);

	const handleVideoClick = () => {
		setIsPlaying(true);
		// start playback as soon as the video element mounts
		// small timeout ensures ref is attached; safe in most cases
		setTimeout(() => {
			if (videoRef.current && typeof videoRef.current.play === "function") {
				videoRef.current.play().catch(() => {
					// play might be blocked by browser autoplay policies; user can press play
				});
			}
		}, 50);
	};

	const handleLearnMore = () => {
		// Do something extra if needed
		navigate("/about-us");
	};

	return (

		<WhoWeAreContainer>
			<div className="whoweare_section">
				<div className="mygrid-container">
					<div className="mygrid-row">
						{/* Left Content */}
						<div className="mygrid-col-lg-6 mygrid-col-md-12">
							<div className="who_we_are_content">
								<h5 className="subheading">Who We Are</h5>
								<h2>
									Welcome to <br />
									<span className="redfont">Subjects Master</span>
								</h2>
								<h4>
									Built for Teachers. <span className="redfont">Loved by Students.</span>
								</h4>
								<p>
									Where custom study guide meet gamified learning, build your quizzes across subjects, tap into our shared global database of educator-created content, engage every
									student through interactive games, and get real-time performance data, all aligned to your standards.
								</p>
								<p>
									We turn traditional studying into an interactive, gamified experience. Students stay motivated and teachers remain in control, all while making learning fun,
									focused, and effective.
								</p>
								<p>
									<strong>You set the goals. | We bring the games. | Your students bring the energy.</strong>
								</p>
								<div className="intro_learnmore">
									<CustomButton onClick={handleLearnMore} text="Learn More" />
								</div>
							</div>
						</div>

						{/* Right Image */}
						<div className="mygrid-col-lg-6 mygrid-col-md-12 ai-center">
							<div className="intro-image-wrapper" onClick={handleVideoClick}>
								{!isPlaying ? (
									<>
										<img
											src={IntroImage}
											alt="Introduction"
											className="intro-image"
										/>
										<img
											src={VideoIconImg}
											alt="Play Video"
											className="video-icon"
										/>
									</>
								) : (
									<video
										ref={videoRef}
										className="intro-video"
										src={AboutUsIntroVideo}
										poster={IntroImage}
										controls
										autoPlay
										muted={false}
										playsInline
									>
										Your browser does not support the video tag.
									</video>
								)}
							</div>
						</div>
					</div>
				</div>

				{/* Intro Boxes */}
				<div className="intro_boxes-wrapper">
					<div className="mygrid-container">
						<div className="mygrid-row">
							<div className="mygrid-col-xl-6 mygrid-col-lg-12 intro-box-col">
								<IntroBox
									bgColor="#84F0CC"
									frontTitle="Teachers"
									frontText="Empowering educators to take control of their instruction with tools designed to enhance, not replace, great teaching."
									backText="Create standards-aligned study guide that also serve as engaging, interactive games."
								/>
							</div>
							<div className="mygrid-col-xl-6 mygrid-col-lg-12 intro-box-col">
								<IntroBox
									bgColor="#ACEBFF"
									frontTitle="At Home"
									frontText="Bringing interactive, game-based learning to your homeschool routine."
									backText="Parents can create custom study guide, reinforce key skills, and keep kids engaged across subjects."
								/>
							</div>
						</div>
					</div>
				</div>
			</div>
		</WhoWeAreContainer>
	);
};

export default WhoWeAre;
